// Phase 3B — records an experience into device-local path memory.
//
// Call from a content surface. Writes nothing on the server, stores no personal
// data, and is safe to call on every render (effect-guarded by ref identity).
import { useEffect } from "react";
import { rememberPath, recordAction } from "@/lib/path-memory";
import type { MemoryKind } from "@/lib/return-loop";

export interface RememberInput {
  kind: MemoryKind;
  ref: string;
  title: string;
  href: string;
  progress?: number;
  completed?: boolean;
  /** Also count this as an experience start (once per mount). */
  countsAsStart?: boolean;
}

export function useRememberPath(input: RememberInput | null): void {
  const key = input ? `${input.kind}:${input.ref}` : "";
  const completed = Boolean(input?.completed);
  useEffect(() => {
    if (!input) return;
    rememberPath({
      kind: input.kind,
      ref: input.ref,
      title: input.title,
      href: input.href,
      progress: input.progress,
      completed: input.completed,
    });
    if (input.countsAsStart) recordAction("start");
    if (input.completed) recordAction("value");
    // Re-runs only when the content or its completion state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, completed]);
}
